import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, UserPlus, KeyRound, Copy, Check, RefreshCw, Loader2, MessageCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

export const generatePassword = () => {
  const arr = new Uint32Array(8);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return `DS@${Array.from(arr, (n) => ALPHABET[n % ALPHABET.length]).join('')}`;
};

const loginUrl = () => (typeof window !== 'undefined' ? window.location.origin : '');

export const credentialsText = (c, name) =>
  `Hi ${name || 'there'}, your DropSquadz dashboard is ready!\n\nLogin: ${loginUrl()}\nEmail: ${c.email}\nPassword: ${c.password}\nClient ID: #${c.clientId}\n\nTap "Log In" on the website and use the above details.`;

/** Plain overlay modal (no Radix scroll-lock) — mobile friendly bottom sheet. */
const Overlay = ({ children, onClose, testId }) => (
  <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" aria-modal="true" data-testid={testId}>
    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92dvh] overflow-y-auto overscroll-contain">
      {children}
    </div>
  </div>
);

export const CredentialsCard = ({ creds, name, phone }) => {
  const [copied, setCopied] = useState('');
  const copy = (txt, key) => {
    try { navigator.clipboard?.writeText(txt)?.catch?.(() => {}); } catch (_e) { /* ignore */ }
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };
  const waPhone = (phone || '').replace(/\D/g, '').slice(-10);
  const waHref = `https://api.whatsapp.com/send/?${waPhone ? `phone=91${waPhone}&` : ''}text=${encodeURIComponent(credentialsText(creds, name))}`;
  const Row = ({ label, value, k, mono }) => (
    <div className="flex items-center justify-between gap-2 bg-white rounded-xl border border-green-200 px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
        <p className={`text-sm font-semibold text-slate-800 truncate ${mono ? 'font-mono' : ''}`} data-testid={`cred-${k}`}>{value}</p>
      </div>
      <button type="button" onClick={() => copy(value, k)} className="no-mobile-pad flex-shrink-0 w-9 h-9 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 flex items-center justify-center" aria-label={`Copy ${label}`}>
        {copied === k ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
  return (
    <div className="space-y-2.5" data-testid="client-credentials-card">
      <Row label="Login Email" value={creds.email} k="email" />
      <Row label="Password" value={creds.password} k="password" mono />
      <Row label="Client ID" value={`#${creds.clientId}`} k="clientid" mono />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
        <Button type="button" variant="outline" onClick={() => copy(credentialsText(creds, name), 'all')} className="w-full" data-testid="cred-copy-all">
          {copied === 'all' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} Copy All Details
        </Button>
        <a href={waHref} target="_blank" rel="noopener noreferrer" className="w-full" data-testid="cred-share-whatsapp">
          <Button type="button" className="w-full bg-green-600 hover:bg-green-700 text-white">
            <MessageCircle className="w-4 h-4 mr-2" /> Send on WhatsApp
          </Button>
        </a>
      </div>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">Save this password now — for security it is not shown again. You can generate a new one anytime.</p>
    </div>
  );
};

const AddClientModal = ({ open, onClose, onCreated }) => {
  const empty = { name: '', email: '', phone: '', password: '', activate: true, plan: '' };
  const [form, setForm] = useState(empty);
  const [showPwd, setShowPwd] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setForm({ ...empty, password: generatePassword() }); setResult(null); setError(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.name.trim().length < 2) return setError('Please enter the client name');
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError('Please enter a valid Gmail / email');
    if (form.phone && form.phone.replace(/\D/g, '').length < 10) return setError('Phone must be 10 digits');
    if (form.password && form.password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/clients`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        password: form.password.trim() || null,
        activate: form.activate,
        plan: form.plan || null,
      });
      setResult(data);
      onCreated?.(data.user);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClose={onClose} testId="add-client-modal">
      <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 sm:px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0"><UserPlus className="w-5 h-5" /></div>
          <div className="min-w-0">
            <h3 className="font-bold text-lg leading-tight">{result ? 'Client Created' : 'Add New Client'}</h3>
            <p className="text-xs text-blue-50 truncate">{result ? 'Share these login details with the client' : 'Create login (Gmail + password) for a client'}</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="no-mobile-pad w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center flex-shrink-0" aria-label="Close" data-testid="add-client-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {result ? (
        <div className="p-4 sm:p-5 space-y-4">
          <div className="rounded-2xl bg-green-50 border border-green-200 p-3 sm:p-4">
            <p className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {result.user?.name} can now log in{result.user?.isActivated ? ' — dashboard unlocked' : ' (₹9 activation pending)'}</p>
            <CredentialsCard creds={result.credentials} name={result.user?.name} phone={result.user?.phone} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={() => { setForm({ ...empty, password: generatePassword() }); setResult(null); }} data-testid="add-another-client">Add Another</Button>
            <Button type="button" onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-client-done">Done</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="p-4 sm:p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Client Name *</label>
            <Input value={form.name} onChange={set('name')} placeholder="e.g. Rahul Sharma" className="mt-1" data-testid="add-client-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Gmail / Email (login ID)</label>
            <Input type="email" inputMode="email" value={form.email} onChange={set('email')} placeholder="client@gmail.com" className="mt-1" data-testid="add-client-email" />
            <p className="text-[11px] text-slate-500 mt-1">Leave blank to auto-generate a login ID like client1007@dropsquadz.com</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">WhatsApp Number</label>
            <Input type="tel" inputMode="numeric" maxLength={13} value={form.phone} onChange={set('phone')} placeholder="10-digit number" className="mt-1" data-testid="add-client-phone" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Password</label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1 min-w-0">
                <Input type={showPwd ? 'text' : 'password'} value={form.password} onChange={set('password')} className="pr-10 font-mono" data-testid="add-client-password" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="no-mobile-pad absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-slate-500" aria-label="Toggle password">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button type="button" variant="outline" onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))} className="flex-shrink-0" data-testid="add-client-generate-password">
                <RefreshCw className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Generate</span>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Plan (optional)</label>
              <select value={form.plan} onChange={set('plan')} className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="add-client-plan">
                <option value="">No plan yet (client picks in onboarding)</option>
                <option value="Basic">Basic</option>
                <option value="Advanced">Advanced</option>
                <option value="Professional">Professional</option>
              </select>
            </div>
            <label className="flex items-start gap-2 rounded-xl border border-slate-200 p-3 cursor-pointer sm:mt-5">
              <input type="checkbox" checked={form.activate} onChange={set('activate')} className="mt-0.5 w-4 h-4 accent-blue-600" data-testid="add-client-activate" />
              <span className="text-sm text-slate-700"><b>Unlock dashboard</b><br /><span className="text-xs text-slate-500">Skip ₹9 activation payment</span></span>
            </label>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="add-client-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 font-semibold" data-testid="add-client-submit">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Create Client & Generate Login
          </Button>
        </form>
      )}
    </Overlay>
  );
};

export const GenerateLoginModal = ({ user, onClose }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creds, setCreds] = useState(null);
  useEffect(() => { if (user) { setPassword(generatePassword()); setCreds(null); setError(''); } }, [user]);
  if (!user) return null;
  const submit = async () => {
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/users/${user.principalId}/generate-login`, { password });
      setCreds(data.credentials);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not generate login');
    } finally { setLoading(false); }
  };
  return (
    <Overlay onClose={onClose} testId="generate-login-modal">
      <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-4 sm:px-5 py-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-lg leading-tight">Generate Login</h3>
          <p className="text-xs text-blue-50 truncate">{user.name} · {user.email}</p>
        </div>
        <button type="button" onClick={onClose} className="no-mobile-pad w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center flex-shrink-0" aria-label="Close" data-testid="generate-login-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 sm:p-5 space-y-3">
        {creds ? (
          <div className="rounded-2xl bg-green-50 border border-green-200 p-3 sm:p-4">
            <CredentialsCard creds={creds} name={user.name} phone={user.phone} />
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">This sets a new password for the client. Their old password will stop working.</p>
            <div className="flex gap-2">
              <Input value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono flex-1 min-w-0" data-testid="generate-login-password" />
              <Button type="button" variant="outline" onClick={() => setPassword(generatePassword())} className="flex-shrink-0" aria-label="Regenerate"><RefreshCw className="w-4 h-4" /></Button>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="button" onClick={submit} disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-login-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Set Password & Show Login
            </Button>
          </>
        )}
      </div>
    </Overlay>
  );
};

export default AddClientModal;
